import { AddExerciseToPatientPayload, TherapyType } from '_api/types';
import {
  DaysOfWeekOptions,
  RegimenOptions,
  RegimenType,
  TargetSideOptions,
  TherapyTypeOptions,
} from '_constants';
import {
  MultipleCheckboxField,
  InputField,
  SelectField,
  ToggleDetailButtons,
  CreateSelectMultipleField,
  PatientInstructionsField,
  PatientDescriptionField,
} from 'components/molecules/formFields';
import { useEffect, useMemo, useState } from 'react';
import { usePatients, useUser } from 'utils/hooks';

import { FormBuilder } from './form-builder';
import { AddToPatientProps } from './types';

export const AddExerciseToPatientForm = ({
  initValue,
  hidePatientSelect = false,
  hideSubmit = false,
  onBack,
  onSubmit,
  onUpdate,
}: AddToPatientProps<AddExerciseToPatientPayload>) => {
  const { isLoading, allPatientOptions, therapistPatientsOptions } = usePatients();
  const { isAdmin } = useUser();
  const [regimenTypeValue, setRegimenTypeValue] = useState<RegimenType>(
    initValue.regimenType,
  );

  useEffect(() => setRegimenTypeValue(initValue.regimenType), [initValue]);

  useEffect(() => {
    const schedules = initValue.schedules;
    if (!schedules || schedules.length === 0) {
      update('perDay', initValue.perDay);
      update('perWeek', initValue.perWeek);
      update('reps', initValue.reps);
      update('sets', initValue.sets);
    } else {
      // Call onUpdate directly to preserve stored schedule days.
      // update('perWeek', ...) would call updateSchedule() and overwrite
      // the patient's custom day pattern with a default.
      const active = schedules[schedules.length - 1];
      onUpdate('schedule', active.schedule);
      onUpdate('perDay', active.perDay);
      onUpdate('perWeek', active.perWeek);
      onUpdate('reps', active.reps);
      onUpdate('sets', active.sets);
    }
  }, [initValue.contentId, initValue.schedules]);

  const adjustedFieldSize = useMemo(
    () =>
      regimenTypeValue === 'HABIT' || regimenTypeValue === ''
        ? 6
        : regimenTypeValue === 'FATIGUE'
          ? 3
          : 2,
    [regimenTypeValue],
  );

  const updateSchedule = (value: number) => {
    switch (value) {
      case 0:
        onUpdate('schedule', []);
        break;
      case 1:
        onUpdate('schedule', ['M']);
        break;
      case 2:
        onUpdate('schedule', ['TU', 'TH']);
        break;
      case 3:
        onUpdate('schedule', ['M', 'W', 'F']);
        break;
      case 4:
        onUpdate('schedule', ['M', 'TU', 'TH', 'F']);
        break;
      case 5:
        onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F']);
        break;
      case 6:
        onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F', 'SA']);
        break;
      default:
        onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F', 'SA', 'SU']);
    }
  };

  const update = async (key: any, arg: any) => {
    if (key === 'regimenType') {
      if (arg === 'HABIT') {
        onUpdate('sets', initValue.sets);
        onUpdate('reps', initValue.reps);
      } else if (arg === 'FATIGUE') {
        onUpdate('reps', initValue.reps);
      }
      setRegimenTypeValue(arg);
    }
    if (key === 'schedule') {
      onUpdate('perWeek', arg.length);
    }
    if (key === 'perWeek') {
      updateSchedule(arg);
    }
    onUpdate(key, arg);
  };

  const patientSelect = {
    component: CreateSelectMultipleField,
    fieldKey: 'patientId',
    label: 'Select a Patient',
    options: isAdmin ? allPatientOptions : therapistPatientsOptions,
    required: true,
    xs: 12,
    readonly: true,
    multiple: false,
  };

  const exerciseFormField = [
    {
      component: ToggleDetailButtons,
      fieldKey: 'regimenType',
      label: 'EXERCISE DETAIL',
      options: RegimenOptions,
      validation: {
        validate: (currentValue: RegimenType) =>
          currentValue != '' || 'Must have an EXERCISE DETAIL selected.',
      },
      xs: 6,
    },
    {
      component: InputField,
      fieldKey: 'reps',
      label: 'REPETITIONS',
      type: 'number',
      xs: 2,
      validation: {
        validate: (currentValue: number) =>
          currentValue > 0 || 'REPETITIONS must be greater than 0.',
      },
    },
    {
      component: InputField,
      fieldKey: 'sets',
      label: 'SETS',
      type: 'number',
      xs: adjustedFieldSize,
      validation: {
        validate: (currentValue: number) =>
          currentValue > 0 || 'SETS must be greater than 0.',
      },
    },
    {
      component: InputField,
      fieldKey: 'perDay',
      label: 'TIMES PER DAY',
      type: 'number',
      xs: adjustedFieldSize,
      validation: {
        validate: (currentValue: number) =>
          currentValue > 0 || 'TIMES PER DAY must be greater than 0.',
      },
    },
    {
      component: SelectField,
      fieldKey: 'defaultTargetSide',
      label: 'TARGET SIDE',
      options: TargetSideOptions || [],
      required: true,
      xs: 2,
    },
    {
      component: InputField,
      fieldKey: 'perWeek',
      label: 'TIMES PER WEEK',
      type: 'number',
      maxValue: 7,
      xs: 2,
      validation: {
        validate: (currentValue: number) =>
          currentValue > 0 || 'TIMES PER WEEK must be greater than 0.',
      },
    },
    {
      component: PatientDescriptionField,
      fieldKey: 'description',
      label: 'Description',
      xs: 12,
    },
    {
      component: PatientInstructionsField,
      fieldKey: 'instructionList',
      label: 'Instructions',
      xs: 12,
      required: true,
      validation: {
        validate: (instructions: string[]) =>
          instructions?.some((instruction) => instruction.trim().length > 0) ||
          'At least one instruction is required.',
      },
    },
    {
      component: MultipleCheckboxField,
      fieldKey: 'schedule',
      label: '',
      options: DaysOfWeekOptions,
      xs: 12,
    },
    {
      component: ToggleDetailButtons,
      fieldKey: 'therapyType',
      label: 'THERAPY TYPE',
      options: TherapyTypeOptions,
      xs: 12,
      validation: {
        validate: (currentValue: TherapyType | '') =>
          currentValue != '' || 'Please select a THERAPY TYPE.',
      },
    },
  ];

  const filterFormFields = useMemo(() => {
    switch (regimenTypeValue) {
      case 'REPETITION':
        return exerciseFormField;
      case 'FATIGUE':
        return exerciseFormField.filter((field) => field.fieldKey != 'reps');
      default:
        return exerciseFormField.filter(
          (field) => field.fieldKey != 'sets' && field.fieldKey != 'reps',
        );
    }
  }, [regimenTypeValue]);

  return !isLoading ? (
    <FormBuilder
      initValues={initValue}
      formFields={
        hidePatientSelect ? filterFormFields : [patientSelect, ...filterFormFields]
      }
      onSubmit={onSubmit}
      onUpdate={update}
      onBack={onBack}
      hideSubmit={hideSubmit}
      backLabel="Cancel"
      submitLabel="Save"
    />
  ) : null;
};
